import { CircleQuestion } from "@gravity-ui/icons";
import {
  Button,
  Card,
  Flex,
  Icon,
  Skeleton,
  Text,
  Tooltip,
} from "@gravity-ui/uikit";
import { useAction, useAtom } from "@reatom/npm-react";
import { PaymentSubscribeRequestDto } from "definitions/poizon-bff";
import { useNavigate } from "react-router-dom";
import { POIZON_API_BOT_LINK } from "src/constants/links";
import { RoutesPaths } from "src/routes/RoutesPaths";
import { isAuthedAtom } from "src/store/atoms/authAtom";
import { subscribeCheckoutAction } from "src/store/fetchers/subscribeCheckout";
import { isOpenPromotionModalAtom } from "src/widgets/ResponsivePromotionWidget/store/atoms/promotionModalAtom";

import { useRateCardTranslate } from "../hooks/useRateCardTranslate";
import { DiscountCountdown } from "./DiscountCountdown";
import css from "./RateCard.module.scss";

interface RateCardButton {
  type: "action" | "contact" | "demo";
  title: string;
  hint?: string;
}

interface RateCardFeature {
  title?: string;
  description?: string;
}

export interface DiscountProps {
  originalPrice: string;
  discountPercent: number;
  timeLeft: number;
  isExpired: boolean;
}

export interface RateCardProps {
  type: string;
  title: string;
  price?: string;
  priceLoading?: boolean;
  button: RateCardButton;
  badge?: string;
  badgeTheme?: "info" | "warning";
  description?: string;
  trial?: boolean;
  featuresAfterTitle?: string;
  features: RateCardFeature[];
  meta?: PaymentSubscribeRequestDto;
  isLanding?: boolean;
  discount?: DiscountProps;
}

export const RateCard = ({
  type,
  title,
  price,
  priceLoading,
  description,
  button,
  badge,
  badgeTheme = "info",
  trial,
  featuresAfterTitle,
  features,
  meta,
  isLanding,
  discount,
}: RateCardProps) => {
  const openPromotion = useAction(isOpenPromotionModalAtom);
  const subscribeCheckout = useAction(subscribeCheckoutAction);
  const i18n = useRateCardTranslate();
  const [isAuthed] = useAtom(isAuthedAtom);
  const navigate = useNavigate();

  const noAuthRequired = button.type === "contact";
  const requiresAuth = !noAuthRequired;

  const getButtonHref = (): string | undefined => {
    if (button.type === "contact") return POIZON_API_BOT_LINK;
    if (requiresAuth && !isAuthed) return RoutesPaths.AUTH.LOGIN;
    if (isLanding) return RoutesPaths.LK.SUBSCRIBE;
    return undefined;
  };

  const buttonHref = getButtonHref();

  const handleButtonClick = () => {
    if (requiresAuth && !isAuthed) {
      navigate(RoutesPaths.AUTH.LOGIN);
      return;
    }

    if (button.type === "demo") openPromotion(true);
    if (button.type === "action" && meta)
      subscribeCheckout({ data: meta, rateName: type });
  };

  return (
    <div className={css.cardWrapper}>
      <Card className={css.card}>
        {badge && (
          <div
            className={`${css.badge} ${badgeTheme === "warning" ? css.badgeWarning : ""}`}
          >
            <Text variant="subheader-1">{badge}</Text>
          </div>
        )}

        <Text variant="subheader-3" color="brand" className={css.title} as="h3">
          {title}
        </Text>
        <Text
          variant="caption-2"
          color="secondary"
          className={css.description}
          as="h4"
        >
          {description}
        </Text>

        <div className={css.priceBlock}>
          {discount && !priceLoading ? (
            <>
              <div className={css.discountHeader}>
                <Text variant="caption-2" className={css.discountBadge}>
                  {i18n.discountBadge(discount.discountPercent)}
                </Text>
                <Text
                  variant="caption-2"
                  color="secondary"
                  className={css.originalPrice}
                >
                  {discount.originalPrice}
                </Text>
              </div>
              <Text variant="header-2" className={css.price}>
                {price}
              </Text>
              {!discount.isExpired && (
                <DiscountCountdown timeLeft={discount.timeLeft} />
              )}
            </>
          ) : (
            <Text variant="header-2" className={css.price}>
              {priceLoading ? (
                <Skeleton className={css.priceSkeleton} />
              ) : (
                price
              )}
            </Text>
          )}
        </div>

        <Button
          size="l"
          view="action"
          target={buttonHref ? "_blank" : undefined}
          {...(buttonHref ? { href: buttonHref } : {})}
          onClick={handleButtonClick}
        >
          {button.title}
        </Button>

        {button.hint && (
          <Text
            variant="caption-1"
            color="secondary"
            className={css.buttonHint}
          >
            {button.hint}
          </Text>
        )}

        {trial ? (
          <Button
            size="xs"
            view="flat"
            href={POIZON_API_BOT_LINK}
            target="_blank"
            className={css.demoButton}
          >
            <Text color="utility-heavy">{i18n.configure}</Text>
          </Button>
        ) : (
          <div className={css.trialSpacer} />
        )}

        <Flex direction="column" gap={2} className={css.featuresList}>
          <Text
            variant="subheader-1"
            color="brand"
            className={css.featuresTitle}
          >
            {featuresAfterTitle}
          </Text>
          {features?.map((feature) => (
            <Tooltip
              key={feature.title}
              content={feature.description}
              openDelay={300}
              placement="top"
            >
              <Flex alignItems="center" gap={2}>
                <Icon data={CircleQuestion} className={css.featureInfo} />
                <Text variant="body-1" as="h5">
                  {feature.title}
                </Text>
              </Flex>
            </Tooltip>
          ))}
        </Flex>
      </Card>
    </div>
  );
};
